import oikosApi from '../../services/oikos'
import qs from 'qs'
import {
  getRegs, insert, update, del, updateField, insOrUp,
} from '../../utils/fc'

// ---------------------------------------------------------------------------
// Catálogo: seções, cláusulas, blocos
// ---------------------------------------------------------------------------

function sortByOrdNome(rows, prefix) {
  return [...(rows || [])].sort((a, b) => {
    const ordA = Number(a[`${prefix}_ord`] ?? 0)
    const ordB = Number(b[`${prefix}_ord`] ?? 0)
    if (ordA !== ordB) return ordA - ordB
    return String(a[`${prefix}_nome`] || '').localeCompare(String(b[`${prefix}_nome`] || ''), 'pt-BR')
  })
}

export async function getSecoes() {
  const rows = await getRegs('minutaSec', {}, {})
  return sortByOrdNome(rows, 'minutaSec')
}

export async function getClausulas() {
  const rows = await getRegs('minutaClaus', {}, {})
  return sortByOrdNome(rows, 'minutaClaus')
}

export async function getBlocos() {
  const rows = await getRegs('minutaBloco', {}, {})
  return sortByOrdNome(rows, 'minutaBloco')
}

export async function getModelos() {
  const rows = await getRegs('minutaMod', {}, {})
  return [...(rows || [])].sort((a, b) => String(a.minutaMod_nome || '').localeCompare(String(b.minutaMod_nome || ''), 'pt-BR'))
}

export async function getModeloItens(minutaModId) {
  const rows = await getRegs('minutaModItem', { minutaMod_id: Number(minutaModId) }, {})
  return [...(rows || [])].sort((a, b) => Number(a.minutaModItem_ord ?? 0) - Number(b.minutaModItem_ord ?? 0))
}

// Escrita ----------------------------------------------------------------

export const saveSecao = (data) => insOrUp('minutaSec', data)
export const saveClausula = (data) => insOrUp('minutaClaus', data)
export const saveBloco = (data) => insOrUp('minutaBloco', data)
export const saveModelo = (data) => insOrUp('minutaMod', data)
export const saveItem = (data) => insOrUp('minutaModItem', data)

export const delSecao = (id) => del('minutaSec', id)
export const delClausula = (id) => del('minutaClaus', id)
export const delBloco = (id) => del('minutaBloco', id)
export const delModelo = (id) => del('minutaMod', id)
export const delItem = (id) => del('minutaModItem', id)

export const setItemOrd = (id, ord) => updateField('minutaModItem', id, 'minutaModItem_ord', ord)
export const setItemPai = (id, paiId) => updateField('minutaModItem', id, 'minutaModItem_paiId', paiId)

export { insert, update, del, updateField }

// ---------------------------------------------------------------------------
// Validação de handler único (frontend)
// ---------------------------------------------------------------------------

const HAND_FIELD = {
  minutaSec: 'minutaSec_hand',
  minutaClaus: 'minutaClaus_hand',
  minutaBloco: 'minutaBloco_hand',
  minutaMod: 'minutaMod_hand',
}

/** Retorna true se o handler já existe em outro registro ativo da tabela. */
export async function handlerExiste(table, hand, ignoreId = null) {
  const field = HAND_FIELD[table]
  if (!field || !hand) return false
  const rows = await getRegs(table, { [field]: hand }, {})
  return (rows || []).some((r) => {
    if (String(r[field]) !== String(hand)) return false // getRegs usa LIKE; confirmar igualdade exata
    if (ignoreId != null && String(r[`${table}_id`]) === String(ignoreId)) return false
    return true
  })
}

export function normalizaHandler(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80)
}

// ---------------------------------------------------------------------------
// Campos da tabela contrato (placeholders dinâmicos)
// ---------------------------------------------------------------------------

const CONTRATO_LABELS = {
  contrato_num: 'Número do contrato',
  contrato_nome: 'Nome do contrato',
  contrato_obj: 'Objeto',
  contrato_vlrGlobal: 'Valor global',
  contrato_dtProp: 'Data da proposta',
  contrato_dtAssin: 'Data de assinatura',
  contrato_prazoVigencia: 'Prazo de vigência',
  contrato_prazoExecucao: 'Prazo de execução',
  contrato_infoPgto: 'Informações de pagamento',
  contrato_obs: 'Observações',
  contratante_label: 'Contratante (nome)',
  contratada_label: 'Contratada (nome)',
}

const CONTRATO_FIELDS_OCULTOS = new Set([
  'contrato_usuarioCria', 'contrato_usuarioMod', 'contrato_dtCria', 'contrato_dtMod',
  'contrato_ativo', 'sp', 'segi',
])

/**
 * Lê as colunas da tabela contrato via action=get_columns e devolve
 * uma lista de placeholders disponíveis: { campo, label }.
 */
export async function getContratoCampos() {
  try {
    const res = await oikosApi.get(`/?${qs.stringify({ action: 'get_columns', table: 'contrato' })}`)
    const cols = res?.data || []
    const campos = cols
      .map((c) => c.Field)
      .filter((f) => f && !CONTRATO_FIELDS_OCULTOS.has(f))
      .map((f) => ({ campo: f, label: CONTRATO_LABELS[f] || f }))
    // Campos derivados úteis (labels resolvidos no dataload de contrato)
    campos.push({ campo: 'contratante_label', label: CONTRATO_LABELS.contratante_label })
    campos.push({ campo: 'contratada_label', label: CONTRATO_LABELS.contratada_label })
    return campos
  } catch (err) {
    console.log('Erro ao carregar campos de contrato', err)
    return []
  }
}

/** Carrega um registro de contrato (com labels) para aplicação de modelo. */
export async function getContrato(contratoId) {
  try {
    const res = await oikosApi.get('https://oikoslab.ecoss.is/oikos/planilha.dataload.php?table=contrato')
    const rows = res?.data || []
    return rows.find((r) => String(r.contrato_id) === String(contratoId)) || null
  } catch (err) {
    console.log('Erro ao carregar contrato', err)
    return null
  }
}
